import React, { useMemo } from 'react';
import { useNodesEdges } from '../contexts/useNodesEdges';
import { getFlowOverview } from '../utils/export-json-flow';
import type { SectionOverview, QuestionOverview } from '@app/services/aiFlow.types';

const ConditionsGrid = ({
    conditions,
}: {
    conditions: Record<string, { about: string; value: string }[]>;
}) => {
    const formatGroup = (group: string[]) => {
        if (group.length === 0) return '';
        if (group.length === 1) return group[0];
        if (group.length === 2) return `${group[0]} and ${group[1]}`;
        return `${group.slice(0, -1).join(', ')}, and ${group.at(-1)}`;
    };
    const formattedGroups = Object.values(conditions).reduce((acc, group) => {
        const formattedConditions = group.map((cond) => {
            return `${cond.about[0].toUpperCase() + cond.about.slice(1)} is "${cond.value}"`;
        });
        return [...acc, formatGroup(formattedConditions)];
    }, [] as string[]);

    if (formattedGroups.length === 0) {
        return null;
    }
    return (
        <div className="conditions-grid" data-testId="conditions-grid">
            {formattedGroups.map((fullCondition, idx) => (
                <React.Fragment key={idx}>
                    <span className="label">{idx === 0 ? 'Visible:' : 'or:'}</span>
                    <span>{fullCondition}</span>
                </React.Fragment>
            ))}
        </div>
    );
};

const QuestionOverView = ({ data }: { data: QuestionOverview }) => {
    const { number, text, conditions, skippable, spellingSensitive, required } = data;
    return (
        <div className={`overview-item question`} data-Testid="question-overview">
            <div className="main-info">
                <span className="order-number">{number}</span>
                <span className="content">{text}</span>
            </div>
            <div className="extra-info">
                <div className="conditions tags">
                    {required && <span className="tag">Required</span>}
                    {skippable && <span className="tag">Skippable</span>}
                    {spellingSensitive && <span className="tag">Spelling Sensitive</span>}
                </div>
                <div className="conditions">
                    <ConditionsGrid conditions={conditions} />
                </div>
            </div>
        </div>
    );
};

const SectionOverView = ({ data }: { data: SectionOverview }) => {
    const { type, text, number, conditions, questions } = data;
    return (
        <div className={`overview-item ${type}`} data-Testid="section-overview">
            <div className="main-info">
                <span className="order-number">{number}</span>
                <span className="content">{text}</span>
            </div>
            <div className="extra-info">
                <div className="conditions">
                    <ConditionsGrid conditions={conditions} />
                </div>
                {!!questions.length && (
                    <div className="questions">
                        {questions.map((question) => (
                            <QuestionOverView data={question} key={question.number} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const FlowOverview = () => {
    const { nodes, edges, flowOrder } = useNodesEdges();

    const flowOverview = useMemo(
        () => getFlowOverview(flowOrder, nodes, edges),
        [nodes, edges, flowOrder]
    );

    return (
        <div className="flow-overview">
            {flowOverview.map((section) => {
                return <SectionOverView data={section} key={section.number} />;
            })}
        </div>
    );
};

export default FlowOverview;
